import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';

/* Material-UI Components */
import {Grid} from "@material-ui/core";
import ListItem from "@material-ui/core/ListItem";

/* BCA modules */
import {getAuthSelector} from "../../../utils/redaxSelectors";
import {PortfolioManagerResource} from "../../../utils/api";
import BaseDialog from "../../../components/BaseDialog/BaseDialog";
import {displaySuccessSnackBar} from "../../../components/SnackbarProvider/actions";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import RadioCard from "../../RiskProfiling/components/StepContent/components/formElement/RadioCard";
import AssetForm from "../AssetForm";
import {getListUrl, getErrorMsg, isExternalContract} from "../utils";
import {useTabsSharedDataContext} from "../../CustomerDashboardV2/components_v2/TabsSharedData/TabsSharedData";
import useFormController from "../hooks/useFormController";
import LinkedItemsSection from "../LinkedItemsSection";
import {ASSETS_LIABILITIES_CONFIG, CATEGORY_NOT_AVAILABLE, LOADING_MSG} from "../constants";
import {getFormScheme, initFormDataForContractWithDefaults} from "../AssetForm/forms_schemes";
import DynamicForm from "../AssetForm/components/DynamicForm";

import useStyles from './styles';


export default connect()((props) => {
  const {
    open,
    onModalClose,
    onAssetCreate,
    customerId,
    dispatch,
    objectType,
    itemsForLinks,
  } = props;

  const assetTypeConfig = ASSETS_LIABILITIES_CONFIG[objectType];
  const categories =  assetTypeConfig.categories;
  const [isSubCategorySelection, setIsSubcategorySelection] = React.useState(true);
  const [currentCategoryOrGroup, setCurrentCategory] = React.useState(categories[0]);
  const [currentSubCategory, setCurrentSubcategory] = React.useState(null);
  const currentCategoryId = React.useMemo(() => {
    return (currentCategoryOrGroup.isGroup || isExternalContract(currentSubCategory)) ? currentSubCategory : currentCategoryOrGroup.id;
  }, [currentCategoryOrGroup, currentSubCategory]);

  const dynamicFormScheme = getFormScheme(currentCategoryOrGroup.id, currentSubCategory, currentCategoryId);

  const [
    formData, setFormData, formDataErrors, setFormDataErrors, checkForUnsavedChanges, linkedItems,
    setLinkedItems, validateForm, handleFormChange, resetForm
  ] = useFormController(dynamicFormScheme);
  const [assetCreationLoading, setAssetCreationLoading] = React.useState(false);

  const sharedData = useTabsSharedDataContext();


  const reset = () => {
    // reset states
    resetForm();
    setIsSubcategorySelection(true);
    setCurrentCategory(categories[0]);
    setCurrentSubcategory(null);
  };

  const handleModalClose = async () => {
    if(!isSubCategorySelection && !await checkForUnsavedChanges(handleCreateAsset)){
      return;
    }

    if (onModalClose) {
      onModalClose();
    }
  };

  const handleSubCategorySave = () => {
    setIsSubcategorySelection(false);

    setFormData((prevState) => {
      const newState = {...prevState, sub_category: currentSubCategory};
      if(dynamicFormScheme) {
        initFormDataForContractWithDefaults(dynamicFormScheme, newState)
      }

      return newState;
    });
  };

  const handleCreateAsset = (close) => {
    if (customerId && currentCategoryId) {
      if(!validateForm()) return;

      setAssetCreationLoading(true);
      const resource = _.get(sharedData, 'resource', PortfolioManagerResource);
      resource.at(getListUrl(customerId, currentCategoryId, formData.sub_category)).post({...formData, links: linkedItems})
      .then((res) => {
        dispatch(displaySuccessSnackBar(<span><b>{res.name}</b> wurde hinzugefügt.</span>));

        if (!_.isNil(onAssetCreate)) {
          onAssetCreate(currentCategoryOrGroup.id, {...res, links: linkedItems}, close);
          if(!close) reset();
        }
      })
      .catch(error => {
        getErrorMsg(dispatch, error, setFormDataErrors);
      }).finally(() => setAssetCreationLoading(false));
    }
  };

  const backToCategorySelection = async () => {
    if(!await checkForUnsavedChanges(handleCreateAsset)){
      return;
    }

    resetForm();
    setIsSubcategorySelection(true);
  };

  return (
    <BaseDialog
      open={open}
      title={assetTypeConfig.addLabel}
      data-id={'other-assets-and-liabilities-create-popup'}
      handleClose={handleModalClose}
      maxWidth={'md'}
      disableEnforceFocus
      modalLoading={assetCreationLoading}
      modalLoadingMsg={LOADING_MSG}
      TransitionProps={{onExited: reset}}
      actions={
        <Grid container spacing={1} justify={"flex-end"}>
          {isSubCategorySelection ? (
            <CategorySelectionActions
              handleModalClose={handleModalClose}
              handleSubCategorySave={handleSubCategorySave}
              saveDisabled={!_.isEmpty(currentCategoryOrGroup.subCategories) && !currentSubCategory}
            />
          ) : (
            <FormActions
              backToCategorySelection={backToCategorySelection}
              handleModalClose={handleModalClose}
              handleCreateAsset={handleCreateAsset}
            />
          )}
        </Grid>
      }
    >
      <Grid container id='asset-menu-modal-tour-element' spacing={2}>
        {isSubCategorySelection ? (
          <CategorySelection
            categories={categories}
            currentCategoryOrGroup={currentCategoryOrGroup}
            setCurrentCategory={setCurrentCategory}
            currentSubCategory={currentSubCategory}
            setCurrentSubcategory={setCurrentSubcategory}
          />
        ) : (
          <FormComponent
            customerId={customerId}
            currentCategoryId={currentCategoryId}
            dynamicFormScheme={dynamicFormScheme}
            formData={formData}
            setFormData={setFormData}
            formDataErrors={formDataErrors}
            handleFormChange={handleFormChange}
            objectType={objectType}
            itemsForLinks={itemsForLinks}
            linkedItems={linkedItems}
            setLinkedItems={setLinkedItems}
          />
        )}
      </Grid>
    </BaseDialog>
  );
});

const INDIVIDUAL_OPTION_VALUE = '1';
const INDIVIDUAL_OPTION = [{id: 1, value: INDIVIDUAL_OPTION_VALUE, label: 'Individuelle Kategorie'}];
const IndividualCategoryRadio = () => (
  <RadioCard
    value={INDIVIDUAL_OPTION_VALUE}
    options={INDIVIDUAL_OPTION}
    onChange={() => {}}
    custom_classes={{
      radioGroupRoot: 'MuiGrid-container',
      formControlLabelRoot: 'MuiFormControlLabel-root MuiGrid-item'
    }}
    newDesign
  />
);

const mapStateToProps = (state) => ({
  auth: getAuthSelector(state),
});

const CategorySelection = connect(mapStateToProps)((props) => {
  const {
    categories, currentSubCategory, setCurrentSubcategory, currentCategoryOrGroup, setCurrentCategory,
    auth: {user}
  } = props;

  const classes = useStyles();
  const isCustomer = _.get(user, 'is_customer');

  const subCategoryOptions = React.useMemo(() => {
    const options = currentCategoryOrGroup.subCategories;
    if(isCustomer && options){
      return options.map(o =>
        isExternalContract(o.value)
          ? {...o, disabled: true, disabledReason: CATEGORY_NOT_AVAILABLE}
          : o
      )
    }
    return options;
  }, [isCustomer, currentCategoryOrGroup])

  return (
    <>
      <Grid item xs={12} sm={'auto'} className={classes.assetMenu}>
        {categories.map((category) => (
          <ListItem
            key={category.id}
            data-id={`category-menu-item-${category.id}`}
            selected={category.id === currentCategoryOrGroup.id}
            onClick={() => {
              setCurrentCategory(category);
              setCurrentSubcategory(null);
            }}
            button
            classes={{
              root: classes.assetMenuItem,
              selected: classes.assetMenuItemSelected
            }}
          >
            <Grid container spacing={1} alignItems={'center'} wrap={"nowrap"}>
              <Grid item className={classes.assetMenuItemIcon}>{category.reactIcon}</Grid>
              <Grid item>{category.title}</Grid>
            </Grid>
          </ListItem>
        ))}
      </Grid>
      <Grid item xs className={classes.subCategories}>
        {_.isEmpty(subCategoryOptions)
          ? <IndividualCategoryRadio />
          : (
            <RadioCard
              value={currentSubCategory || ''}
              required
              options={subCategoryOptions}
              onChange={setCurrentSubcategory}
              custom_classes={{
                radioGroupRoot: 'MuiGrid-container',
                formControlLabelRoot: 'MuiFormControlLabel-root MuiGrid-item'
              }}
              newDesign
            />
          )
        }
      </Grid>
    </>
  )
});

const CategorySelectionActions = ({handleModalClose, handleSubCategorySave, saveDisabled}) => {
  return (
    <>
      <Grid item data-id={'cancel-btn-wrapper'}>
        <PrimaryButton onButtonClick={handleModalClose} color="secondary" text={'Abbrechen'} variant={'outlined'}/>
      </Grid>
      <Grid item data-id={'submit-btn-wrapper'}>
        <PrimaryButton
          onButtonClick={handleSubCategorySave}
          text={'Weiter'}
          disabled={saveDisabled}
        />
      </Grid>
    </>
  )
};

const FormComponent = (props) => {
  const {
    customerId, currentCategoryId, dynamicFormScheme, formData, setFormData, formDataErrors,
    handleFormChange, objectType, itemsForLinks, linkedItems, setLinkedItems
  } = props;

  return (
    <>
      {!!dynamicFormScheme ? (
        <DynamicForm
          schema={dynamicFormScheme}
          defaults={formData}
          handleFormChange={handleFormChange}
          errors={formDataErrors}
        />
      ) : (
        <AssetForm
          categoryId={currentCategoryId}
          defaults={{sub_category: formData.sub_category}}
          setFormData={setFormData}
          formDataErrors={formDataErrors}
          customerId={customerId}
        />
      )}
      <LinkedItemsSection
        objectType={objectType}
        itemsForLinks={itemsForLinks}
        currentLinkedItems={linkedItems}
        setLinkedItems={setLinkedItems}
        mainItemTypeId={currentCategoryId}
      />
    </>
  )
};

const FormActions = ({backToCategorySelection, handleModalClose, handleCreateAsset}) => {
  const onSave = React.useCallback(() => handleCreateAsset(), [handleCreateAsset]);
  const onSaveAndClose = React.useCallback(() => handleCreateAsset(true), [handleCreateAsset]);

  return (
    <>
      <Grid item style={{marginRight: 'auto'}} data-id={'back-btn-wrapper'}>
        <PrimaryButton
          onButtonClick={backToCategorySelection}
          text={'Zurück'}
          icon={<i className={'fal fa-long-arrow-left'} />}
          variant={'text'}
        />
      </Grid>
      <Grid item data-id={'cancel-btn-wrapper'}>
        <PrimaryButton onButtonClick={handleModalClose} color="secondary" text={'Abbrechen'} variant={'outlined'} />
      </Grid>
      <Grid item data-id={'submit-btn-wrapper'}>
        <PrimaryButton onButtonClick={onSave} text={'Speichern'} />
      </Grid>
      <Grid item data-id={'submit-btn-wrapper'}>
        <PrimaryButton onButtonClick={onSaveAndClose} text={'Speichern & schließen'} variant={'outlined'} />
      </Grid>
    </>
  )
};